import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Pagination,
} from "@mui/material";
import axios from "axios";

interface Task {
    id: string;
    key?: string;
    title: string;
    project?: { name?: string };
}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const TaskSearchResults: React.FC = () => {
    const query = useQuery().get("query") || "";
    const history = useHistory();

    const [results, setResults] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 20;

    useEffect(() => {
        if (!query) return;

        const fetchResults = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`/tasks-search`, {
                    params: { query, page, pageSize },
                });

                setResults(res.data.data || []);
                setTotal(res.data.meta?.total || 0);
            } catch (err) {
                console.error("Failed to fetch search results", err);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [query, page]);

    const handleSelectTask = (task: Task) => {
        history.push({
            pathname: "/tasks",
            state: { taskToEdit: task },
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Search results for: "{query}"
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            ) : results.length === 0 ? (
                <Typography>No matching tasks found.</Typography>
            ) : (
                <>
                    <List>
                        {results.map((task) => (
                            <React.Fragment key={task.id}>
                                <ListItem button onClick={() => handleSelectTask(task)}>
                                    <ListItemText
                                        primary={`${task.key ?? task.id}: ${task.title}`}
                                        secondary={task.project?.name}
                                    />
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>

                    {total > pageSize && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                                count={Math.ceil(total / pageSize)}
                                page={page}
                                onChange={(_, newPage) => setPage(newPage)}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default TaskSearchResults;
